import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MultiSelect, Popover } from '@mantine/core';
import { type DatesRangeValue } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { IoChevronDown } from 'react-icons/io5';
import { productsApi } from '@modules/products/api/products.api';
import type { ProductCardResponse } from '@modules/products/types';
import { sellersApi } from '@modules/sellers/api/sellers.api';
import { FinanceDateRangeInput } from '@repo/date-range-picker';
import { Checkbox } from '@shared/ui';

export type ReviewsPageFilters = {
  sellerIds: number[];
  productIds: number[];
  ratings: number[];
  dateFrom: string;
  dateTo: string;
  hasPhotos: boolean;
};

export const EMPTY_REVIEWS_FILTERS: ReviewsPageFilters = {
  sellerIds: [],
  productIds: [],
  ratings: [],
  dateFrom: '',
  dateTo: '',
  hasPhotos: false,
};

const REVIEWS_FILTERS_STORAGE_KEY = 'admin-reviews-filters';

const RATING_OPTIONS = [5, 4, 3, 2, 1].map((rating) => ({
  value: String(rating),
  label: String(rating),
}));

function isYmd(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function positiveIds(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is number => typeof id === 'number' && Number.isInteger(id) && id > 0);
}

export function readStoredReviewsFilters(): ReviewsPageFilters {
  try {
    const raw = localStorage.getItem(REVIEWS_FILTERS_STORAGE_KEY);
    if (!raw) return EMPTY_REVIEWS_FILTERS;
    const parsed = JSON.parse(raw) as Partial<ReviewsPageFilters>;
    const ratings = Array.isArray(parsed.ratings)
      ? parsed.ratings.filter(
          (rating): rating is number =>
            typeof rating === 'number' && Number.isInteger(rating) && rating >= 1 && rating <= 5,
        )
      : [];
    let dateFrom = isYmd(parsed.dateFrom) ? parsed.dateFrom : '';
    let dateTo = isYmd(parsed.dateTo) ? parsed.dateTo : '';
    if (dateFrom && dateTo && dateFrom > dateTo) {
      [dateFrom, dateTo] = [dateTo, dateFrom];
    }
    return {
      sellerIds: positiveIds(parsed.sellerIds),
      productIds: positiveIds(parsed.productIds),
      ratings: [...new Set(ratings)],
      dateFrom,
      dateTo,
      hasPhotos: parsed.hasPhotos === true,
    };
  } catch {
    return EMPTY_REVIEWS_FILTERS;
  }
}

export function writeStoredReviewsFilters(filters: ReviewsPageFilters) {
  localStorage.setItem(REVIEWS_FILTERS_STORAGE_KEY, JSON.stringify(filters));
}

export function reviewsFiltersSelectedCount(filters: ReviewsPageFilters): number {
  return (
    filters.sellerIds.length +
    filters.productIds.length +
    filters.ratings.length +
    (filters.dateFrom || filters.dateTo ? 1 : 0) +
    (filters.hasPhotos ? 1 : 0)
  );
}

function toYmd(value: Date | string | null): string {
  if (!value) return '';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function fromYmd(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

type ReviewsFiltersPopoverProps = {
  value: ReviewsPageFilters;
  selectedCount: number;
  onChange: (filters: ReviewsPageFilters) => void;
};

export default function ReviewsFiltersPopover({
  value,
  selectedCount,
  onChange,
}: ReviewsFiltersPopoverProps) {
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!opened) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest(
          '.reviews-page__filters-trigger, .reviews-filters-popover, .mantine-Popover-dropdown, .mantine-Combobox-dropdown',
        )
      ) {
        return;
      }
      setOpened(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [opened]);

  const sellersQuery = useQuery({
    queryKey: ['sellers'],
    queryFn: () => sellersApi.getAll(),
    enabled: opened,
  });
  const productsQuery = useQuery({
    queryKey: ['products', 'list', 'reviews-filters'],
    queryFn: () => productsApi.getAll(),
    enabled: opened,
  });

  const shops = useMemo(() => {
    return (sellersQuery.data ?? [])
      .slice()
      .sort((a, b) => a.shopName.localeCompare(b.shopName, 'ru'));
  }, [sellersQuery.data]);

  const products = useMemo(() => {
    const raw = productsQuery.data;
    const list: ProductCardResponse[] = Array.isArray(raw) ? raw : raw?.products ?? [];
    return list.slice().sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [productsQuery.data]);

  const rangeValue: DatesRangeValue = [fromYmd(value.dateFrom), fromYmd(value.dateTo)];

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      shadow="md"
      offset={8}
      withinPortal
      closeOnClickOutside={false}
      trapFocus={false}
      classNames={{ dropdown: 'reviews-filters-popover' }}
    >
      <Popover.Target>
        <button
          type="button"
          className={`reviews-page__filters-trigger${selectedCount > 0 ? ' reviews-page__filters-trigger--active' : ''}${opened ? ' reviews-page__filters-trigger--open' : ''}`}
          aria-label="Фильтры отзывов"
          aria-expanded={opened}
          onClick={() => setOpened((current) => !current)}
        >
          <span className="reviews-page__filters-trigger-label">Фильтры</span>
          {selectedCount > 0 ? (
            <span className="reviews-page__filters-badge">{selectedCount}</span>
          ) : null}
          <IoChevronDown size={14} aria-hidden />
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <div className="reviews-filters">
          <div className="reviews-filters__header">
            <button
              type="button"
              className="reviews-filters__reset"
              disabled={selectedCount === 0}
              onClick={() => onChange(EMPTY_REVIEWS_FILTERS)}
            >
              Сбросить все
            </button>
          </div>
          <FinanceDateRangeInput
            label="Дата"
            value={rangeValue}
            onChange={(range) => {
              const [from, to] = range;
              let dateFrom = toYmd(from);
              let dateTo = toYmd(to);
              if (dateFrom && dateTo && dateFrom > dateTo) {
                [dateFrom, dateTo] = [dateTo, dateFrom];
              }
              onChange({ ...value, dateFrom, dateTo });
            }}
            valueFormat="DD.MM.YYYY"
            placeholder="Выберите даты"
            size="xs"
            radius="md"
            style={{ width: '100%' }}
            popoverProps={{ zIndex: 400, withinPortal: true }}
          />
          <MultiSelect
            className="multiselect"
            label="Товар"
            size="xs"
            placeholder={value.productIds.length > 0 ? undefined : 'Все товары'}
            searchable
            clearable
            data={products.map((product) => ({ value: String(product.id), label: product.name }))}
            value={value.productIds.map(String)}
            onChange={(values) =>
              onChange({
                ...value,
                productIds: values.map(Number).filter((id) => Number.isInteger(id) && id > 0),
              })
            }
            nothingFoundMessage="Товары не найдены"
            comboboxProps={{ zIndex: 1100 }}
          />
          <MultiSelect
            className="multiselect"
            label="Магазин"
            size="xs"
            placeholder={value.sellerIds.length > 0 ? undefined : 'Все магазины'}
            searchable
            clearable
            data={shops.map((seller) => ({ value: String(seller.id), label: seller.shopName }))}
            value={value.sellerIds.map(String)}
            onChange={(values) =>
              onChange({
                ...value,
                sellerIds: values.map(Number).filter((id) => Number.isInteger(id) && id > 0),
              })
            }
            nothingFoundMessage="Магазины не найдены"
            comboboxProps={{ zIndex: 1100 }}
          />
          <MultiSelect
            className="multiselect"
            label="Рейтинг"
            size="xs"
            placeholder={value.ratings.length > 0 ? undefined : 'Все'}
            clearable
            data={RATING_OPTIONS}
            value={value.ratings.map(String)}
            onChange={(values) =>
              onChange({
                ...value,
                ratings: values
                  .map(Number)
                  .filter((rating) => Number.isInteger(rating) && rating >= 1 && rating <= 5),
              })
            }
            comboboxProps={{ zIndex: 1100 }}
          />
          <Checkbox
            label="С фото"
            checked={value.hasPhotos}
            onChange={(event) => onChange({ ...value, hasPhotos: event.currentTarget.checked })}
          />
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
